import {MorphoNode, Path, position} from './typings';
import {getMorphoService, serviceNames} from '@/rte/editor';
import {getEditor} from './utils';
import {getCurrentCard} from './getCurrentCard';

const getNodeChildrenFromContent = (content: string | string[] | MorphoNode[] | undefined) => {
    if (!content) {
        return '';
    }
    const editor = getEditor();
    if (!editor) {
        return '';
    }
    if (typeof content === 'string') {
        return editor.mdSerializer.deserialize(content);
    }
    if (Array.isArray(content) && typeof content[0] === 'string') {
        // 结点数组的数组
        return content.map(eachContent => editor.mdSerializer.deserialize(eachContent));
    }
    // 认为是结点数组
    return content as MorphoNode[];
};

const generateNodeByType = (type: string, content: any, attributes?: any): any => {
    // 字符串数组
    const contentTextArray = Array.isArray(content) && typeof content[0] === 'string' ? content : null;
    // 字符串二维数组
    const contentTextsArray: string[][] | null = Array.isArray(content) && Array.isArray(content[0]) ? content : null;
    // 结点数组
    const contentNodes = !contentTextsArray && typeof content === 'object' ? content : null;
    // 单纯字符串
    const singleContentText: string
        = !contentTextArray && !contentTextsArray && !contentNodes && content.toString() || null;
    switch (type) {
        case 'paragraph': {
            const node = {
                type: 'paragraph',
                children: contentNodes || [{text: singleContentText}],
                ...attributes,
            };
            return node;
        }
        case 'heading': {
            const node = {
                type: 'heading',
                level: 1,
                children: contentNodes || [{text: singleContentText}],
                ...attributes,
            };
            return node;
        }
        case 'table': {
            let tableRows = [];
            if (contentNodes) {
                tableRows = contentNodes;
            }
            else if (contentTextsArray) {
                tableRows = contentTextsArray.map((row) => {
                    return generateNodeByType('table-row', row.map((cell) => {
                        const cellChildren = getNodeChildrenFromContent(cell);
                        return generateNodeByType('table-cell', cellChildren);
                    }));
                });
            }
            else {
                tableRows = [
                    generateNodeByType('table-row', [generateNodeByType('table-cell', singleContentText)]),
                ];
            }

            const node = {
                type: 'table',
                children: tableRows,
                data: {
                    width: new Array(tableRows[0].children.length).fill(100),
                },
                ...attributes,
            };
            return node;
        }
        case 'table-row': {
            let rowChildren = [];
            if (contentNodes) {
                rowChildren = contentNodes;
            }
            else if (contentTextArray) {
                rowChildren = contentTextArray.map((cell) => generateNodeByType('table-cell', cell));
            }
            else {
                rowChildren = [generateNodeByType('table-cell', singleContentText)];
            }
            const node = {
                type: 'table-row',
                children: rowChildren,
                ...attributes,
            };
            return node;
        }
        case 'table-cell': {
            const node = {
                type: 'table-cell',
                children: contentNodes || [generateNodeByType('paragraph', singleContentText)],
                data: {
                    colspan: 1,
                    rowspan: 1,
                },
                ...attributes,
            };
            return node;
        }
        case 'ordered-list':
            break;
        case 'unordered-list':
            break;
        case 'todo-list':
            break;
    }
    // 默认为一个 void 结点，直接使用传入的 type 和属性
    return {
        type,
        ...attributes,
        children: [{text: ''}],
    };
};

/* eslint-disable max-statements */
/* eslint-disable complexity */
const getInsertPath = (editor: any, at: string, builderId: string, type: string) => {
    let insertPath: Path = [];
    const hasCardNode = editor.children.some((node: any) => node.type === 'card');
    switch (at) {
        case 'start':
            // 文档起始位置插入
            // todo 判断文档首个结点是否为块引用
            insertPath = hasCardNode ? [1, 0, 0] : [1];
            if (type === 'card') {
                insertPath = [1];
            }
            break;
        case 'end': {
            // 文档末尾插入
            const lastNode = editor.children[editor.children.length - 1];
            if (lastNode.type === 'card') {
                insertPath = [editor.children.length - 1, 0, lastNode.children[0].children.length];
            }
            else {
                insertPath = [editor.children.length];
            }
            if (type === 'card') {
                insertPath = [editor.children.length];
            }
            break;
        }
        case 'cardStart': {
            const currentCard = getCurrentCard(builderId);
            if (currentCard) {
                insertPath = currentCard[1].concat(0, 0);
            }
            if (type === 'card') {
                insertPath = currentCard ? currentCard[1].slice(0, 1) : [1];
            }
            break;
        }
        case 'cardEnd': {
            const currentCard = getCurrentCard(builderId);
            if (currentCard) {
                insertPath = currentCard[1].concat(0, currentCard[0].children[0].children.length);
            }
            if (type === 'card') {
                insertPath = [editor.children.length];
            }
            break;
        }
        case 'before': {
            const flexBuilderNode = editor.MorphoAPI.findNodeById(editor, builderId, 'builderId');
            if (flexBuilderNode && flexBuilderNode[1]) {
                insertPath = flexBuilderNode[1].slice(0);
            }
            if (type === 'card') {
                const currentCard = getCurrentCard(builderId);
                if (currentCard) {
                    insertPath = currentCard[1].slice(0, 1);
                }
                else if (flexBuilderNode) {
                    insertPath = flexBuilderNode[1].slice(0, 1);
                }
            }
            break;
        }
        case 'after': {
            const flexBuilderNode = editor.MorphoAPI.findNodeById(editor, builderId, 'builderId');
            if (flexBuilderNode && flexBuilderNode[1]) {
                insertPath = flexBuilderNode[1].slice(0);
                insertPath[insertPath.length - 1]++;
            }
            if (type === 'card') {
                const currentCard = getCurrentCard(builderId);
                if (currentCard) {
                    insertPath = currentCard[1].slice(0, 1);
                    insertPath[insertPath.length - 1]++;
                }
                else if (flexBuilderNode) {
                    insertPath = flexBuilderNode[1].slice(0, 1);
                    insertPath[insertPath.length - 1]++;
                }
            }
            break;
        }
    }
    return insertPath;
};

/* eslint-disable complexity */
const getInsertMethod = (builderId: string) => {

    return (
        // 插入类型
        type: string,
        at: position,
        options: {
            // 添加到结点上的属性
            attributes?: any;
            // 结点的内容，可以为字符串、字符串数组、也可以为结点数组
            content?: string | string[] | MorphoNode[];
            // markdown 文本，当传入 markdown 时，type 和 content 会被忽略，只按照 markdown 解析结果插入结点
            // html 和 markdown 只能二选一，优先 html
            markdown?: string;
            // html 文本，当传入 html 时，type 和 content 会被忽略，只按照 html 解析结果插入结点
            // html 和 markdown 只能二选一，优先 html
            html?: string;
        }
    ) => {
        const editor = getEditor();
        if (!editor) {
            return;
        }
        const transformService = getMorphoService(serviceNames.ITransformService);
        let insertPath: Path = [];

        if (Array.isArray(at)) {
            insertPath = at;
        }
        else if (typeof at === 'string' && !['start', 'end', 'before', 'after', 'cardStart', 'cardEnd'].includes(at)) {
            const atNode = editor.MorphoAPI.findNodeById(editor, at);
            if (atNode?.[1]) {
                insertPath = atNode[1].slice(0);
                insertPath[insertPath.length - 1]++;
            }
        }
        else {
            insertPath = getInsertPath(editor, at, builderId, type);
        }
        if (!insertPath || insertPath.length === 0) {
            return;
        }
        if (options?.html) {
            const nodes = editor.htmlSerializer.deserialize(options.html);
            transformService.insertNodes(nodes, {at: insertPath});
            return;
        }
        if (options?.markdown) {
            const nodes = editor.mdSerializer.deserialize(options.markdown);
            transformService.insertNodes(nodes, {at: insertPath});
            return;
        }
        if (Array.isArray(options?.content) && !Array.isArray(options?.content[0])
            && typeof options.content[0] !== 'string' && !(options.content[0] instanceof File)) {
            // 此场景认为是结点数组
            transformService.insertNodes(options.content, {at: insertPath});
            return;
        }
        if (type === 'card') {
            const cardService = getMorphoService(serviceNames.ICardService);
            if (cardService) {
                const children = getNodeChildrenFromContent(options?.markdown || options?.content);
                cardService.insertCard({
                    path: insertPath,
                    children,
                    ...options?.attributes,
                });
            }
            return;
        }
        if (type === 'layout') {
            const layoutService = getMorphoService(serviceNames.ILayoutService);
            if (layoutService) {
                const children = getNodeChildrenFromContent(options?.markdown || options?.content);
                layoutService.insertLayout(
                    editor,
                    options?.attributes?.view || 'column',
                    children?.length || 3,
                    {
                        at: insertPath,
                        children,
                        ...options?.attributes,
                    }
                );
            }
            return;
        }
        if (type === 'image') {
            const imageService = getMorphoService(serviceNames.IImageService);
            if (imageService) {
                imageService.insertImages(options?.content, {
                    at: insertPath,
                    ...options?.attributes,
                });
            }
            return;
        }
        const node = generateNodeByType(type, options?.content || '', options?.attributes || null);
        node && node.children.length > 0 && transformService.insertNodes(node, {at: insertPath});
    };
};

// debug 测试用
// const testInsert = getInsertMethod('test-builder');
// (window as any).testInsert = testInsert;

const insertRow = (tableId: string, rowContent: string[]|any[], index?: number) => {
    const editor = getEditor();
    if (!editor) {
        return;
    }
    const tableNode = editor.MorphoAPI.findNodeById(editor, tableId);
    if (!tableNode) {
        return;
    }
    let rowChildren = [];
    if (typeof rowContent[0] === 'string' || typeof rowContent[0] === 'number') {
        rowChildren = rowContent.map(cellText => generateNodeByType('table-cell', cellText));
    }
    else {
        rowChildren = rowContent;
    }
    const rowNode = generateNodeByType('table-row', rowChildren);
    const transformService = getMorphoService(serviceNames.ITransformService);
    index = typeof index === 'number' ? index : tableNode[0].children.length as number;
    transformService.insertNodes(rowNode, {at: tableNode[1].concat(index)});
};

export {getInsertMethod, insertRow};
